import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@environments/environment';
import { CompleteCompanyModel } from '@one/models/company.model';
import { buildCompanyPreview, COMPANY_PREVIEW_SITE_NAME, ICompanyPreview, TCompanyPreviewSource } from '@shared/utils/company-preview.utils';

/**
 * Título, descrição e Open Graph da página da empresa.
 * Roda no SERVIDOR (companyPreviewResolver, para os robôs de prévia, que não executam JS) e no NAVEGADOR
 * (CompanyComponent, depois do /complete). Sempre updateTag + property=: o addTag só reaproveitava a tag
 * quando TODOS os atributos eram iguais, e duplicava as tags ao trocar de empresa na SPA.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title: Title = inject(Title);
  private readonly meta: Meta = inject(Meta);
  private readonly document: Document = inject(DOCUMENT);
  private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));

  /** Chamado pelo CompanyComponent com o /complete. Mantém a troca do favicon pelo logo (só no navegador). */
  public setMetaTags(company: CompleteCompanyModel | null | undefined): void {
    this.applyCompany(company?.company);

    const logoUrl: string | undefined = company?.company?.logo?.url;
    if (!this.isBrowser || !logoUrl) return;

    const favicon: HTMLLinkElement | null = this.document.getElementById('favicon') as HTMLLinkElement | null;
    if (favicon) favicon.href = logoUrl;
  }

  /** Escreve as tags da empresa. Sem nome ou sem slug, devolve false e não toca em nada. */
  public applyCompany(company: TCompanyPreviewSource | null | undefined): boolean {
    const preview: ICompanyPreview | null = buildCompanyPreview(company, environment.storeUrl);
    if (!preview) return false;

    this.title.setTitle(preview.title);
    this.meta.updateTag({ name: 'description', content: preview.description });

    this.setProperty('og:site_name', COMPANY_PREVIEW_SITE_NAME);
    this.setProperty('og:type', 'website');
    this.setProperty('og:title', preview.title);
    this.setProperty('og:description', preview.description);
    this.setProperty('og:url', preview.url);
    this.setProperty('og:image', preview.image);
    this.setProperty('og:image:secure_url', preview.image);
    this.setProperty('og:image:alt', preview.imageAlt);
    // Logo é quadrado: "summary" mostra miniatura quadrada; "summary_large_image" cortaria o logo em 2:1.
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });

    this.setCanonical(preview.url);
    return true;
  }

  private setProperty(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }

  private setCanonical(url: string): void {
    let link: HTMLLinkElement | null = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }
}
